import { api } from '../api/client';

// Photos are served from a different origin than the app (Cloudflare R2 in
// production), which has no CORS policy configured — loading them with
// crossOrigin='anonymous' (required for canvas pixel access: face detection,
// Fabric.js collage compositing, anything that reads pixels back out) silently
// fails there, even though the plain <img> display tag works fine. Route
// cross-origin photos through the backend's same-origin proxy
// (GET /faces/photo-proxy) instead, so the browser never needs cross-origin
// canvas access at all. Same-origin paths (local dev) and blob:/data: URLs
// (in-memory previews) are already safe and returned unchanged.
//
// Extracted from useFaceDetection.ts so Collage Creator's canvas rendering
// reuses the exact same CORS-safe path instead of duplicating it — this bug
// class (silently-tainted canvas) previously cost multiple sessions to
// diagnose the first time it was hit.
// The proxy route validates by host/origin only (R2 pattern / S3_PUBLIC_URL),
// never by content-type — it's really "fetch any of our own R2 asset bytes
// same-origin", not image-specific. Returns the raw Blob (not a blob: URL) so
// callers that need to hand it across a realm boundary — e.g. postMessage to
// a Web Worker, see transcribeAudio.ts — can do so safely: blob: URLs aren't
// reliably shareable across worker boundaries in every browser engine, but a
// Blob is a structured-cloneable type everywhere.
// This app's CSP connect-src (self + nominatim + jsdelivr only) blocks
// fetch() against a data: URL outright — decode the base64 payload by hand
// instead, which never goes through connect-src at all. blob: URLs are
// fine to fetch() (Chromium doesn't subject them to connect-src), so only
// data: needs this path.
export function dataUrlToBlobSync(dataUrl: string): Blob {
  const commaIndex = dataUrl.indexOf(',');
  const header = dataUrl.slice(0, commaIndex);
  const base64 = dataUrl.slice(commaIndex + 1);
  const mimeMatch = /data:(.*?);base64/.exec(header);
  const mime = mimeMatch?.[1] || 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

async function fetchSafeAssetBlob(src: string): Promise<Blob> {
  if (src.startsWith('data:')) return dataUrlToBlobSync(src);
  const res = await fetch(src);
  return res.blob();
}

export async function resolveSafeAssetBlob(src: string): Promise<Blob> {
  if (src.startsWith('blob:') || src.startsWith('data:')) return fetchSafeAssetBlob(src);
  if (!/^https?:\/\//i.test(src)) return fetchSafeAssetBlob(src);
  try {
    if (new URL(src).origin === window.location.origin) return fetchSafeAssetBlob(src);
  } catch {
    // fall through to proxy
  }
  const { data } = await api.get<Blob>('/faces/photo-proxy', { params: { url: src }, responseType: 'blob' });
  return data;
}

export async function resolveSafeImageSrc(src: string): Promise<{ url: string; revoke?: () => void }> {
  if (src.startsWith('blob:') || src.startsWith('data:')) return { url: src };
  if (!/^https?:\/\//i.test(src)) return { url: src };
  try {
    if (new URL(src).origin === window.location.origin) return { url: src };
  } catch {
    // fall through to proxy
  }
  const blob = await resolveSafeAssetBlob(src);
  const objectUrl = URL.createObjectURL(blob);
  return { url: objectUrl, revoke: () => URL.revokeObjectURL(objectUrl) };
}

export async function loadImageSafely(src: string): Promise<HTMLImageElement> {
  const { url, revoke } = await resolveSafeImageSrc(src);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => { revoke?.(); resolve(img); };
    img.onerror = (e) => { revoke?.(); reject(e); };
    img.src = url;
  });
}
